import {Box, HStack, StyleProps, Text, VStack} from '@chakra-ui/react';
import {useLocation, useNavigate} from 'react-router-dom';
import {DisplayLabels} from './DisplayLabels';
import {ListeDrapeaux} from '../components/listeDrapeaux';
import {ReactNode} from 'react';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {isItemStateCatalogueFF, routes} from 'ff-common';
import NiceModal from '@ebay/nice-modal-react';
import {ApercuProduitModale} from '@/components/Modale/ApercuProduitModale';
import {VignetteArticleVStack} from '@/domains/mediatheque/VignetteArticleVStack';
import {ArticleInfoTooltip} from '@/domains/ligneProduit/ArticleInfoTooltip';
import {BoutonDLCStock} from '@/domains/ligneProduit/BoutonDLCStock';
import {EncartDLC} from '@/domains/ligneProduit/EncartDLC';
import {MargesProduit} from '@/domains/ligneProduit/MargesProduit';
import {Navigable, State} from '@synako/halitrek';
import {useHalState} from '@synako/haligator';
import {
  ArticleDesc,
  DisponibiliteArticleDesc,
  LigneDePanierDesc,
} from 'api-types/ffconnect-hal';
import ColdIcon from '@/components/svg/ColdIcon';
import {useTranslation} from 'react-i18next';
import {useClient} from '@/haligator-factory';
import {ArticleCodesDisplay} from '../components/ArticleCodesDisplay';
import {usePeutVoirDlcStock} from '@/hooks/auth/usePermissionsCommerciales';
import {TagCadencier} from '@/domains/cadencier/components/TagCadencier';
import {useEstDansCadencier} from '@/hooks/cadencier/useEstDansCadencier';

export const ArticleBlock: React.FC<
  {
    code_article: string;
    dispo?: Navigable<DisponibiliteArticleDesc>;
    precommande?: ReactNode;
    libelleFallback?: string;
    // Article lu quand l'item du catalogue ne répond pas (article sorti du catalogue du client)
    articleFallback?: Navigable<ArticleDesc>;
    onProductClick?: (code_article: string) => void;
    isBackoffice?: boolean;
    skipItemCatalogue?: boolean;
    libelles_tronques?: boolean;
    ligneDePanier?: State<LigneDePanierDesc>;
    // Affiche le tag « Cadencier » si l'article est au cadencier du client.
    afficherTagCadencier?: boolean;
    /** Prix en cours de saisie (preview marges). */
    prixDraft?: number | null;
    /** Panier PDF : bouton DLC/stock même si la dispo ligne est déjà suivie. */
    forcerBoutonDlcStock?: boolean;
  } & StyleProps
> = ({
  code_article,
  dispo: navDispo,
  precommande,
  libelleFallback,
  articleFallback,
  onProductClick,
  isBackoffice = false,
  skipItemCatalogue = false,
  libelles_tronques = false,
  ligneDePanier,
  afficherTagCadencier = false,
  prixDraft,
  forcerBoutonDlcStock = false,
  ...props
}) => {
  const clientNav = useClient();
  const clientState = useHalState(clientNav);
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const estDansCadencier = useEstDansCadencier();

  const {buildClientRoute} = useClientRoute();
  const routeToArticle = buildClientRoute(routes.client.produit, {
    code_article,
  });
  const itemNav = useClient().follow('go:item_catalogue_2', {
    marketplace_id: 'FF',
    code: code_article,
  });
  const itemState = useHalState(skipItemCatalogue ? undefined : itemNav);
  const articleNav: Navigable<ArticleDesc> | undefined =
    itemState.state?.follow('article') ?? articleFallback;
  const articleState = useHalState(articleNav);
  const toledeData = isItemStateCatalogueFF(itemState.state)
    ? itemState.state.data.tolede
    : undefined;
  const itemUniteDeCommande = itemState.data?.unite_de_commande_client;

  // Specificités propre au commercial
  const peutVoirDlcStock = usePeutVoirDlcStock();

  const canClick = !!itemState.data;

  const libelle =
    articleState.data?.tolede.libelle_commercial ?? libelleFallback ?? '';

  // Tags sur une 2ᵉ ligne uniquement s'il y en a au moins un — sinon pas d'espace vide.
  const montreProduitMarche =
    Boolean(toledeData?.produit_marche) &&
    clientState.data?.affichage_produit_marche === 'active';
  const montreCadencier =
    afficherTagCadencier && estDansCadencier(code_article);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onProductClick) {
      onProductClick(code_article);
    } else if (canClick) {
      NiceModal.show(ApercuProduitModale, {code_article, location});
    } else if (!itemState.error) {
      // Fallback si l'item n'est pas encore chargé
      // En erreur (article sorti du catalogue du client), la fiche afficherait la même erreur
      navigate(routeToArticle);
    }
  };

  return (
    <HStack
      alignItems='start'
      cursor={(onProductClick ?? canClick) ? 'pointer' : 'not-allowed'}
      onClick={handleClick}
      minW={0}
      w='100%'
      {...props}
      position='relative'
    >
      {precommande}
      <Box
        position='absolute'
        top='55px'
        left='0px'
      >
        {articleState.data?.temperature_livraison === 'negatif' && (
          <ColdIcon boxSize='25px' />
        )}
      </Box>
      <Box
        alignSelf='stretch'
        display='flex'
      >
        <VignetteArticleVStack article={articleNav} />
      </Box>
      <VStack
        alignItems='start'
        minW={0}
        flex='1'
      >
        {/* Ligne méta : codes + marges + tooltip (inchangée si pas de tags). */}
        <HStack
          alignItems='center'
          spacing={2}
          flexWrap='wrap'
        >
          <ArticleCodesDisplay
            code_article={code_article}
            toledeData={toledeData}
          />
          {/* Marges visibles pour le commercial : automatiquement si l'article est au panier,
              ou à la demande (clic sur le picto calculatrice) si hors panier. */}
          <MargesProduit
            code_article={code_article}
            ligneDePanier={ligneDePanier}
            prixDraft={prixDraft}
          />
          <ArticleInfoTooltip
            dispo={navDispo}
            itemUniteDeCommande={itemUniteDeCommande}
            isBackoffice={isBackoffice}
          />
        </HStack>
        {/* Ligne tags : absente s'il n'y a rien à afficher → pas de régression d'espacement. */}
        {(montreProduitMarche || montreCadencier) && (
          <HStack
            alignItems='center'
            spacing={2}
            flexWrap='wrap'
          >
            {montreProduitMarche && (
              <Text
                color='blue.main'
                fontSize='xs'
                fontWeight='bold'
                lineHeight='short'
              >
                {t('global.produit_marche')}
              </Text>
            )}
            {montreCadencier && <TagCadencier code_article={code_article} />}
          </HStack>
        )}
        {/* Desktop */}
        <Box
          display={{base: 'none', md: 'block'}}
          w='100%'
        >
          <Text
            fontWeight='600'
            lineHeight='16px'
            fontSize='sm'
            noOfLines={libelles_tronques ? 1 : undefined}
          >
            {libelle}
          </Text>
        </Box>
        <HStack>
          {(articleState.data?.tolede.marque?.[0] ?? '').length > 1 && (
            <Text
              display={{base: 'none', md: 'block'}}
              lineHeight='16px'
              maxH='32px'
              maxW='400px'
              fontSize='sm'
            >
              {articleState.data?.tolede.marque?.[0]}
            </Text>
          )}
          <DisplayLabels
            SIQO_et_regimes_alimentaires={
              articleState.data?.tolede.SIQO_et_regimes_alimentaires
            }
          />
          <ListeDrapeaux
            listepays={articleState.data?.tolede.pays_d_origine ?? []}
          />
        </HStack>
        {/* Mobile */}
        <Box
          display={{base: 'block', md: 'none'}}
          w='100%'
        >
          <Text
            fontWeight='600'
            lineHeight='16px'
            noOfLines={libelles_tronques ? 1 : undefined}
          >
            {libelle}&nbsp;{articleState.data?.tolede.marque?.[0]}
          </Text>
        </Box>
        {peutVoirDlcStock && (!navDispo || forcerBoutonDlcStock) && (
          <BoutonDLCStock
            itemNav={itemNav}
            itemUniteDeCommande={itemUniteDeCommande}
          />
        )}
        <EncartDLC dispo={navDispo} />
      </VStack>
    </HStack>
  );
};
